import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { apiGet } from './services/api';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { KioskCodeEntryPage } from './pages/KioskCodeEntryPage';
import { KioskIntakePage } from './pages/KioskIntakePage';
import { KioskSuccessPage } from './pages/KioskSuccessPage';
import { FoodCheckInPage } from './pages/FoodCheckInPage';
import { FoodDistributionPage } from './pages/FoodDistributionPage';
import { FDClientDetailPage } from './pages/FDClientDetailPage';
import { FDReferralsPage } from './pages/FDReferralsPage';
import { FDReportsPage } from './pages/FDReportsPage';
import { ClientIntakePage } from './pages/ClientIntakePage';
import { ClientListPage } from './pages/ClientListPage';
import { ClientDetailPage } from './pages/ClientDetailPage';
import { DonorListPage } from './pages/DonorListPage';
import { AddDonorPage } from './pages/AddDonorPage';
import { RecordDonationPage } from './pages/RecordDonationPage';
import { LogServiceVisitPage } from './pages/LogServiceVisitPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { ReportsPage } from './pages/ReportsPage';
import { CANSAssessmentPage } from './pages/CANSAssessmentPage';
import FamilyListPage from './pages/FamilyListPage';
import FamilyCreatePage from './pages/FamilyCreatePage';
import FamilyDetailPage from './pages/FamilyDetailPage';
import { SystemConfigurationPage } from './pages/SystemConfigurationPage';
import { AuditLogsPage } from './pages/AuditLogsPage';
import { DuplicatesPage } from './pages/DuplicatesPage';
import { AnnouncementsPage } from './pages/AnnouncementsPage';
import { UserProfilePage } from './pages/UserProfilePage';
import { AnnouncementBanner } from './components/AnnouncementBanner';
import { AppFooter } from './components/AppFooter';
import { GenerateKioskCode } from './components/GenerateKioskCode';
import { UserRole } from './types/auth';
import logo from './assets/logo_golden_white.png';
import './App.css';

export const APP_VERSION = '1.8.0';

function App() {
  const navigate = useNavigate();

  // Listen for auth timeout events and navigate to login
  useEffect(() => {
    const handleAuthTimeout = () => {
      navigate('/login');
    };
    window.addEventListener('auth:timeout', handleAuthTimeout);
    return () => window.removeEventListener('auth:timeout', handleAuthTimeout);
  }, [navigate]);

  return (
    <Routes>
      {/* Public auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Public kiosk routes (no auth required) */}
      <Route path="/kiosk" element={<KioskCodeEntryPage />} />
      <Route path="/kiosk/intake" element={<KioskIntakePage />} />
      <Route path="/kiosk/success" element={<KioskSuccessPage />} />

      {/* Public food distribution check-in (no auth required) */}
      <Route path="/food/check-in" element={<FoodCheckInPage />} />

      {/* Protected routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function MainLayout() {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  const closeUserMenu = () => {
    setUserMenuOpen(false);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    closeUserMenu();
  };

  const handleLogout = () => {
    closeUserMenu();
    logout();
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        userMenuOpen &&
        !target.closest('.user-menu-button') &&
        !target.closest('.user-dropdown-menu')
      ) {
        closeUserMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  return (
    <div className="App">
      <header className="App-header">
        <button className="mobile-menu-button" onClick={toggleMobileMenu}>
          {mobileMenuOpen ? '✕' : '☰'}
        </button>
        <div className="App-header-content">
          <div className="App-header-logo">
            <img src={logo} alt="Belafonte TACOLCY Center" className="header-logo" />
          </div>
          <div className="App-header-user">
            {user && (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={toggleUserMenu}
                  className="user-menu-button"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    padding: '0.5rem 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.95rem',
                  }}
                >
                  <span className="user-info">
                    {user.firstName} {user.lastName} ({user.role})
                  </span>
                  <span style={{ fontSize: '0.8rem' }}>▼</span>
                </button>

                {userMenuOpen && (
                  <div
                    className="user-dropdown-menu"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      background: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      minWidth: '200px',
                      marginTop: '0.5rem',
                      zIndex: 1000,
                    }}
                  >
                    <button
                      onClick={handleProfileClick}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '0.75rem 1rem',
                        textAlign: 'left',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: '#333',
                        fontSize: '0.95rem',
                        borderBottom: '1px solid #eee',
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                      onMouseOut={(e) => (e.currentTarget.style.background = 'none')}
                    >
                      👤 My Profile
                    </button>
                    <button
                      onClick={handleLogout}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '0.75rem 1rem',
                        textAlign: 'left',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: '#D32F2F',
                        fontSize: '0.95rem',
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                      onMouseOut={(e) => (e.currentTarget.style.background = 'none')}
                    >
                      🚪 Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <nav className="App-nav">
        <div className={`App-nav-content ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          {/* Mobile user info - shown only when menu is open */}
          {mobileMenuOpen && user && (
            <div className="mobile-user-info">
              <strong>
                {user.firstName} {user.lastName}
              </strong>{' '}
              ({user.role})
            </div>
          )}
          <NavLink to="/dashboard" onClick={closeMobileMenu}>
            Dashboard
          </NavLink>
          <NavLink to="/clients" onClick={closeMobileMenu}>
            Clients
          </NavLink>
          <NavLink to="/families" onClick={closeMobileMenu}>
            Families
          </NavLink>
          <NavLink to="/donors" onClick={closeMobileMenu}>
            Donors
          </NavLink>
          <NavLink to="/food-distribution" onClick={closeMobileMenu}>
            Food Distribution
          </NavLink>
          <NavLink to="/reports" onClick={closeMobileMenu}>
            Reports
          </NavLink>
          {user?.role === UserRole.ADMIN && (
            <NavLink to="/admin" onClick={closeMobileMenu}>
              Admin
            </NavLink>
          )}
          <NavLink to="/profile" onClick={closeMobileMenu}>
            My Profile
          </NavLink>
          {/* Mobile logout button */}
          {mobileMenuOpen && (
            <button
              className="mobile-logout"
              onClick={() => {
                closeMobileMenu();
                logout();
              }}
            >
              Sign Out
            </button>
          )}
        </div>
      </nav>

      <main>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/clients" element={<ClientListPage />} />
          <Route path="/clients/intake" element={<ClientIntakePage />} />
          <Route path="/clients/:clientId" element={<ClientDetailPage />} />
          <Route path="/clients/:clientId/cans/new" element={<CANSAssessmentPage />} />
          <Route path="/clients/log-visit" element={<LogServiceVisitPage />} />
          <Route path="/cans/:assessmentId" element={<CANSAssessmentPage />} />
          <Route path="/families" element={<FamilyListPage />} />
          <Route path="/families/create" element={<FamilyCreatePage />} />
          <Route path="/families/:familyID" element={<FamilyDetailPage />} />
          <Route path="/donors" element={<DonorListPage />} />
          <Route path="/donors/add" element={<AddDonorPage />} />
          <Route path="/donors/donate" element={<RecordDonationPage />} />
          <Route path="/food-distribution" element={<FoodDistributionPage />} />
          <Route path="/food-distribution/referrals" element={<FDReferralsPage />} />
          <Route path="/food-distribution/reports" element={<FDReportsPage />} />
          <Route path="/food-distribution/:fdClientId" element={<FDClientDetailPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/profile" element={<UserProfilePage />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
                <UserManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/config"
            element={
              <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
                <SystemConfigurationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit-logs"
            element={
              <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
                <AuditLogsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/duplicates"
            element={
              <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
                <DuplicatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/announcements"
            element={
              <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
                <AnnouncementsPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>

      <AppFooter className="App-footer" />
    </div>
  );
}

// Navigation Link Component with active state
function NavLink({
  to,
  children,
  onClick,
}: {
  to: string;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');

  return (
    <Link to={to} className={isActive ? 'active' : ''} onClick={onClick}>
      {children}
    </Link>
  );
}

// Page Components
function DashboardPage() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<{
    totalClients: number;
    activeFamilies: number;
    serviceHours: number;
    cansDue: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const resp = await apiGet<{
          totalClients: number;
          activeFamilies: number;
          serviceHours: number;
          cansDue: number;
        }>('/reports/dashboard');
        if (resp.success) {
          setMetrics(resp.data);
        } else {
          console.error('Dashboard API error:', resp.error?.message);
        }
      } catch (err) {
        console.error('Failed to fetch dashboard metrics:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();

    // Background refetching: refetch every 5 minutes
    const interval = setInterval(
      () => {
        fetchDashboard();
      },
      5 * 60 * 1000
    ); // 5 minutes

    // Refetch on window focus
    const handleFocus = () => {
      fetchDashboard();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  return (
    <div>
      <div className="page-header">
        <h2>Dashboard</h2>
        <p>Welcome back, {user?.firstName}! Here&apos;s your overview</p>
      </div>

      {/* Announcements Banner */}
      <AnnouncementBanner maxVisible={3} />

      {loading ? (
        <p>Loading dashboard metrics...</p>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1.5rem',
          }}
        >
          <div className="card">
            <h4>Total Clients Served</h4>
            <p
              style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--color-primary-purple)' }}
            >
              {metrics?.totalClients || 0}
            </p>
          </div>

          <div className="card">
            <h4>Active Families</h4>
            <p
              style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--color-primary-purple)' }}
            >
              {metrics?.activeFamilies || 0}
            </p>
          </div>

          <div className="card">
            <h4>Service Hours (MTD)</h4>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--color-primary-gold)' }}>
              {metrics?.serviceHours || 0}
            </p>
          </div>

          <div className="card">
            <h4>CANS Due</h4>
            <p
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: metrics?.cansDue ? '#F44336' : 'var(--color-primary-purple)',
              }}
            >
              {metrics?.cansDue || 0}
            </p>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: '1.5rem' }}>
        <h3>Quick Actions</h3>
        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
          <Link to="/clients/intake">
            <button className="button-primary">New Client Intake</button>
          </Link>
          <Link to="/donors/donate">
            <button className="button-secondary">Record Donation</button>
          </Link>
          <Link to="/clients/log-visit">
            <button className="button-primary">Log Service Visit</button>
          </Link>
        </div>
      </div>

      {/* Kiosk Code Generator */}
      <GenerateKioskCode />
    </div>
  );
}

export default App;
